
import React from 'react';
import { Button } from '@/components/ui/button';
import { Check, Trash2 } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { ResponsesTable } from './ResponsesTable';
import { ResponsesCards } from './ResponsesCards';
import { useIsMobile } from '@/hooks/use-mobile';
import { Separator } from '@/components/ui/separator';

interface ResponsesPanelProps {
  filteredResponses: any[];
  selectedItems: string[];
  setSelectedItems: React.Dispatch<React.SetStateAction<string[]>>;
  selectAllItems: () => void;
  toggleItemSelection: (id: string) => void;
  handleDelete: (id: string) => void;
  handleMultipleDelete: () => void;
  isDeleteModalOpen: boolean;
  setIsDeleteModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

export const ResponsesPanel: React.FC<ResponsesPanelProps> = ({
  filteredResponses,
  selectedItems,
  setSelectedItems,
  selectAllItems,
  toggleItemSelection,
  handleDelete,
  handleMultipleDelete,
  isDeleteModalOpen,
  setIsDeleteModalOpen
}) => {
  const isMobile = useIsMobile();

  return (
    <div className="space-y-4">
      <Separator className="my-4" />
      
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <Button 
          variant="outline" 
          size="sm" 
          onClick={selectAllItems} 
          className="flex items-center gap-2 w-full sm:w-auto"
        >
          {selectedItems.length === filteredResponses.length && filteredResponses.length > 0 ? (
            <>
              <Check className="h-4 w-4" />
              Unselect All
            </>
          ) : (
            <>
              <Check className="h-4 w-4" />
              Select All
            </>
          )}
        </Button>
        {selectedItems.length > 0 && (
          <Dialog open={isDeleteModalOpen} onOpenChange={setIsDeleteModalOpen}>
            <DialogTrigger asChild>
              <Button 
                variant="destructive" 
                size="sm" 
                className="flex items-center gap-2 w-full sm:w-auto"
              >
                <Trash2 className="h-4 w-4" />
                Delete Selected ({selectedItems.length})
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Confirm Deletion</DialogTitle>
                <DialogDescription>
                  Are you sure you want to delete {selectedItems.length} selected item(s)? This action cannot be undone.
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button variant="outline" onClick={() => setIsDeleteModalOpen(false)}>Cancel</Button>
                <Button variant="destructive" onClick={handleMultipleDelete}>Delete</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="mt-6">
        {isMobile ? (
          <ResponsesCards 
            filteredResponses={filteredResponses}
            selectedItems={selectedItems}
            toggleItemSelection={toggleItemSelection}
            handleDelete={handleDelete}
          />
        ) : (
          <ResponsesTable 
            filteredResponses={filteredResponses}
            selectedItems={selectedItems}
            toggleItemSelection={toggleItemSelection}
            handleDelete={handleDelete}
          />
        )}
      </div>
    </div>
  );
};
